import { Prominences } from '@sede-enterprise/shell-ds-fabrics';
import {
  BaseTable,
  Button,
  Sizes,
  Text,
  TextInput,
} from '@sede-enterprise/shell-ds-react-framework';
import { getSourceKey } from '@see-power-pft-common-js/utils/sourceKey';
import type { ColumnDef } from '@tanstack/react-table';
import { roundValue } from 'pages/Availability/common/AvailabilityInputRow';
import {
  AssetInputErrors,
  PowerEnergyInput,
} from 'pages/Availability/common/FormDrawer/types';
import { getLatestAssetLimit } from 'pages/Availability/common/utils';
import { Asset, AssetLimit } from 'pages/Availability/types';
import { ReactNode, useCallback, useMemo, useState } from 'react';

interface AssetLimitsTableProps {
  assets: Asset[];
  selectedAssets: string[]; // Array of assetSourceKeys
  assetInputs: Record<string, PowerEnergyInput>;
  onChange: (assetSourceKey: string, inputs: Partial<PowerEnergyInput>) => void;
  onApplyToAll?: (inputs: PowerEnergyInput) => void;
  disabled?: boolean;
  errors?: Record<string, AssetInputErrors>;
}

type TableRow = {
  assetSourceKey: string;
  assetName: string;
  operationalLimit: AssetLimit | null;
  maxExportPower: string | null;
  maxImportPower: string | null;
  energyCapacity: string | null;
  errors?: AssetInputErrors;
};

type ApplyToAllRow = PowerEnergyInput & { label: string };

const formatLimit = (limit: AssetLimit | null): string => {
  if (!limit) return '-';
  return `${limit.maxExportPower} MW / ${limit.energyCapacity} MWh`;
};

const TableCell = ({ children }: { children: ReactNode }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '130px' }}>
      {children}
    </div>
  );
};

export const AssetLimitsTable = ({
  assets,
  selectedAssets,
  assetInputs,
  onChange,
  onApplyToAll,
  disabled = false,
  errors = {},
}: AssetLimitsTableProps) => {
  const [applyToAllValues, setApplyToAllValues] = useState<PowerEnergyInput>({
    maxExportPower: null,
    maxImportPower: null,
    energyCapacity: null,
  });

  const applyToAllRow = useMemo<ApplyToAllRow>(
    () => ({
      label: 'Apply to all',
      ...applyToAllValues,
    }),
    [applyToAllValues],
  );

  const tableData = useMemo<TableRow[]>(() => {
    const rows: TableRow[] = [];
    for (const sourceKey of selectedAssets) {
      const asset = assets.find((a) => getSourceKey(a) === sourceKey);
      if (!asset) continue;

      const inputs = assetInputs[sourceKey] ?? {
        maxExportPower: null,
        maxImportPower: null,
        energyCapacity: null,
      };
      rows.push({
        assetSourceKey: sourceKey,
        assetName: asset.name,
        operationalLimit: getLatestAssetLimit(asset.assetLimits),
        maxExportPower: inputs.maxExportPower,
        maxImportPower: inputs.maxImportPower,
        energyCapacity: inputs.energyCapacity,
        errors: errors[sourceKey],
      });
    }
    return rows;
  }, [assets, selectedAssets, assetInputs, errors]);

  const handleInputChange = useCallback(
    (
      assetSourceKey: string,
      field: 'maxExportPower' | 'maxImportPower' | 'energyCapacity',
      value: string,
    ) => {
      const roundedValue = roundValue(value);
      const valueToPush =
        roundedValue.length > value.length ? value : roundedValue;
      onChange(assetSourceKey, { [field]: valueToPush || null });
    },
    [onChange],
  );

  const handleApplyToAllChange = useCallback(
    (
      field: 'maxExportPower' | 'maxImportPower' | 'energyCapacity',
      value: string,
    ) => {
      const roundedValue = roundValue(value);
      const valueToPush =
        roundedValue.length > value.length ? value : roundedValue;
      setApplyToAllValues((prev) => ({
        ...prev,
        [field]: valueToPush || null,
      }));
    },
    [],
  );

  const applyToAllColumns: ColumnDef<ApplyToAllRow>[] = useMemo(
    () => [
      {
        accessorKey: 'label',
        cell: ({ row }) => (
          <TableCell>
            <Button
              type="button"
              variant="outlined"
              sentiment="positive"
              disabled={disabled}
              onClick={() =>
                onApplyToAll?.({
                  maxExportPower: row.original.maxExportPower,
                  maxImportPower: row.original.maxImportPower,
                  energyCapacity: row.original.energyCapacity,
                })
              }
              size={Sizes.Small}
            >
              Apply to all
            </Button>
          </TableCell>
        ),
      },
      {
        accessorKey: 'maxExportPower',
        cell: ({ row }) => (
          <TableCell>
            <TextInput
              size={Sizes.Small}
              suffix={{ node: 'MW' }}
              type="number"
              min={0}
              value={row.original.maxExportPower ?? ''}
              onChange={(e) =>
                handleApplyToAllChange('maxExportPower', e.target.value)
              }
              disabled={disabled}
            />
          </TableCell>
        ),
      },
      {
        accessorKey: 'maxImportPower',
        cell: ({ row }) => (
          <TableCell>
            <TextInput
              size={Sizes.Small}
              suffix={{ node: 'MW' }}
              type="number"
              min={0}
              value={row.original.maxImportPower ?? ''}
              onChange={(e) =>
                handleApplyToAllChange('maxImportPower', e.target.value)
              }
              disabled={disabled}
            />
          </TableCell>
        ),
      },
      {
        accessorKey: 'energyCapacity',
        cell: ({ row }) => (
          <TableCell>
            <TextInput
              size={Sizes.Small}
              suffix={{ node: 'MWh' }}
              type="number"
              min={0}
              value={row.original.energyCapacity ?? ''}
              onChange={(e) =>
                handleApplyToAllChange('energyCapacity', e.target.value)
              }
              disabled={disabled}
            />
          </TableCell>
        ),
      },
    ],
    [disabled, handleApplyToAllChange, onApplyToAll],
  );

  const columns: ColumnDef<TableRow>[] = useMemo(
    () => [
      {
        header: 'Asset',
        accessorKey: 'assetName',
        cell: ({ row }) => (
          <TableCell>
            <Text>{row.original.assetName}</Text>
            <Text size={Sizes.Small} prominence={Prominences.Subtle}>
              {formatLimit(row.original.operationalLimit)}
            </Text>
          </TableCell>
        ),
      },
      {
        header: 'Export limit',
        accessorKey: 'maxExportPower',
        cell: ({ row }) => (
          <TableCell>
            <TextInput
              size={Sizes.Small}
              suffix={{ node: 'MW' }}
              type="number"
              min={0}
              value={row.original.maxExportPower ?? ''}
              onChange={(e) =>
                handleInputChange(
                  row.original.assetSourceKey,
                  'maxExportPower',
                  e.target.value,
                )
              }
              disabled={disabled}
            />
            {row.original.errors?.maxExportPower && (
              <Text
                size={Sizes.Small}
                style={{ color: 'rgba(221, 29, 33, 1)' }}
              >
                {row.original.errors.maxExportPower}
              </Text>
            )}
          </TableCell>
        ),
      },
      {
        header: 'Import limit',
        accessorKey: 'maxImportPower',
        cell: ({ row }) => (
          <TableCell>
            <TextInput
              size={Sizes.Small}
              suffix={{ node: 'MW' }}
              type="number"
              min={0}
              value={row.original.maxImportPower ?? ''}
              onChange={(e) =>
                handleInputChange(
                  row.original.assetSourceKey,
                  'maxImportPower',
                  e.target.value,
                )
              }
              disabled={disabled}
            />
            {row.original.errors?.maxImportPower && (
              <Text
                size={Sizes.Small}
                style={{ color: 'rgba(221, 29, 33, 1)' }}
              >
                {row.original.errors.maxImportPower}
              </Text>
            )}
          </TableCell>
        ),
      },
      {
        header: 'Energy limit',
        accessorKey: 'energyCapacity',
        cell: ({ row }) => (
          <TableCell>
            <TextInput
              size={Sizes.Small}
              suffix={{ node: 'MWh' }}
              type="number"
              min={0}
              value={row.original.energyCapacity ?? ''}
              onChange={(e) =>
                handleInputChange(
                  row.original.assetSourceKey,
                  'energyCapacity',
                  e.target.value,
                )
              }
              disabled={disabled}
            />
            {row.original.errors?.energyCapacity && (
              <Text
                size={Sizes.Small}
                style={{ color: 'rgba(221, 29, 33, 1)' }}
              >
                {row.original.errors.energyCapacity}
              </Text>
            )}
            {row.original.errors?.limits && (
              <Text
                size={Sizes.Small}
                style={{ color: 'rgba(221, 29, 33, 1)' }}
              >
                {row.original.errors.limits}
              </Text>
            )}
          </TableCell>
        ),
      },
    ],
    [disabled, handleInputChange],
  );

  if (selectedAssets.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '16px' }}>
      <BaseTable
        columns={applyToAllColumns}
        data={[applyToAllRow]}
        noHeader
        tableOptions={{
          enableSorting: false,
        }}
      />
      <div style={{ height: '8px' }} />
      <div data-testid="asset-limits-table">
        <BaseTable
          columns={columns}
          data={tableData}
          emptyStateProps={{
            description: 'Select assets to enter limits',
          }}
        />
      </div>
    </div>
  );
};
